import { useUser } from 'components/providers/UserProvider'
import { useLocalForageState } from 'lib/hooks/useLocalForageState'
import { FetchedStockInterface } from 'lib/hooks/useStockStore'
import { useSnackbar } from 'notistack'
import { useCallback, useEffect, useRef } from 'react'
import { InventoryArticle } from 'shared'
import {
  ListingTabId,
  ListingTabMeta,
  ListingTabsState,
  makeInitialTabsState,
} from 'utils/constants'

/**
 *
 * - tabsState collects the number of active tabs in the listing, also indicating the lot number they are linked to, if any.
 * - allListingStocks creates a connection between tabState and the stock inserted in that tab.
 * - legacyListing is the old object in local storage that referenced what the user had listed but not saved. It is needed during deployment to make the first starting tab work.
 * - listingStock is essentially a sort of currentStock.
 */

const makeEmptyFetchedStock = (): FetchedStockInterface => ({
  stock: [],
  sealedStock: [],
  fetchedAt: new Date().toISOString(),
})

export const BASE_TAB_ID: ListingTabId = 1 as ListingTabId
export const MAX_LISTING_TABS = 8

export const useListingTabsWithStocks = () => {
  const { user } = useUser()
  const { enqueueSnackbar } = useSnackbar()

  const prunedOnceRef = useRef(false)

  //Tab status (id/title/active)
  const [tabsState, setTabsState, loadingTabs] = useLocalForageState<ListingTabsState>(
    makeInitialTabsState(),
    `${user?.username}-listing-tabs`
  )

  // Map tabId -> FetchedStockInterface
  const [allListingStocks, setAllListingStocks, loadingStocks] = useLocalForageState<
    Record<ListingTabId, FetchedStockInterface>
  >({}, `${user?.username}-listing-stocks`, {
    hydrate: (value) => {
      if (!value) return {}

      const result: Record<ListingTabId, FetchedStockInterface> = {}

      Object.entries(value as Record<string, FetchedStockInterface>).forEach(([key, stock]) => {
        const numericKey = Number(key) as ListingTabId

        result[numericKey] = {
          ...stock,
          stock: (stock.stock || []).map((article: any) => new InventoryArticle(article)),
          sealedStock: (stock.sealedStock || []).map(
            (article: any) => new InventoryArticle(article)
          ),
        }
      })

      return result
    },
  })

  // Legacy listing (old `${username}-file` key), is used to make everything work on deployment
  const [legacyListing, , legacyLoading] = useLocalForageState<FetchedStockInterface>(
    { stock: [], sealedStock: [], fetchedAt: '' },
    `${user?.cardmarketUsername}-file`,
    {
      duckFunction: (value) => value?.stock?.find?.((article: any) => article?.card?._id),
      hydrate: (value) =>
        value
          ? {
              ...value,
              stock: (value.stock || []).map((article: any) => {
                return new InventoryArticle(article)
              }),
              sealedStock: (value.sealedStock || []).map((article: any) => {
                return new InventoryArticle(article)
              }),
            }
          : { stock: [], sealedStock: [], fetchedAt: '' },
    }
  )

  // listingStock of the active tab + setter
  const activeListingTabId = tabsState.activeTabId

  const listingStock: FetchedStockInterface =
    allListingStocks[activeListingTabId] ?? makeEmptyFetchedStock()

  const setListingStock = useCallback(
    (next: FetchedStockInterface | ((prev: FetchedStockInterface) => FetchedStockInterface)) => {
      setAllListingStocks((prevMap) => {
        const currentForTab = prevMap[activeListingTabId] ?? makeEmptyFetchedStock()

        const updatedForTab =
          typeof next === 'function'
            ? (next as (p: FetchedStockInterface) => FetchedStockInterface)(currentForTab)
            : next

        return {
          ...prevMap,
          [activeListingTabId]: updatedForTab,
        }
      })
    },
    [setAllListingStocks, activeListingTabId]
  )

  // helpers id tab
  const getNextTabId = useCallback((tabs: ListingTabsState['tabs']): number => {
    if (!tabs.length) return 1
    const maxId = Math.max(...tabs.map((t) => t.id))
    return maxId + 1
  }, [])

  /**
   * initialize the tab with empty stock
   */
  const addListingTab = useCallback(() => {
    setTabsState((state) => {
      if (state.tabs.length >= MAX_LISTING_TABS) {
        return state
      }

      const nextId = getNextTabId(state.tabs)
      const newTab = {
        id: nextId,
        title: `Tab ${nextId}`,
      }

      setAllListingStocks((prevStocks) => ({
        ...prevStocks,
        [nextId]: makeEmptyFetchedStock(),
      }))

      return {
        ...state,
        tabs: [...state.tabs, newTab],
        activeTabId: nextId,
      }
    })
  }, [setTabsState, setAllListingStocks, getNextTabId])

  /**
   * mainly used for view purposes to change the tab style and display its stock
   */
  const setActiveListingTab = useCallback(
    (id: ListingTabId) => {
      setTabsState((state) => ({
        ...state,
        activeTabId: id,
      }))
    },
    [setTabsState]
  )

  const renameListingTab = (id: ListingTabId, title: string) => {
    const trimmed = title.trim()
    if (!trimmed) return

    const tabs = tabsState.tabs
    const target = tabs.find((t) => t.id === id)
    if (!target) return

    if (target.lotId !== undefined) {
      enqueueSnackbar('You cannot rename a tab already linked to a lot', { variant: 'warning' })
      return
    }

    const nameAlreadyUsed = tabs.some(
      (t) => t.id !== id && t.title.toLowerCase() === trimmed.toLowerCase()
    )

    if (nameAlreadyUsed) {
      enqueueSnackbar('A tab with this name already exists', { variant: 'warning' })
      return
    }

    const isTabZero = id === BASE_TAB_ID

    if (!isTabZero) {
      setTabsState((state) => ({
        ...state,
        tabs: state.tabs.map((tab) => (tab.id === id ? { ...tab, title: trimmed } : tab)),
      }))
      return
    }

    // caso Tab Zero
    if (tabs.length >= MAX_LISTING_TABS) {
      enqueueSnackbar(
        'You cannot rename the tab; the maximum number of open tabs has been reached. Please close some tabs before renaming.',
        { variant: 'warning' }
      )
      return
    }

    const nextId = getNextTabId(tabs)

    // Stock migration (pure logic only)
    setAllListingStocks((prevStocks) => {
      const baseStock = prevStocks[BASE_TAB_ID] ?? makeEmptyFetchedStock()
      const baseHasContent =
        (baseStock.stock?.length ?? 0) > 0 || (baseStock.sealedStock?.length ?? 0) > 0

      const targetStock = prevStocks[nextId]
      const targetHasContent =
        targetStock &&
        ((targetStock.stock?.length ?? 0) > 0 || (targetStock.sealedStock?.length ?? 0) > 0)

      if (!baseHasContent || targetHasContent) {
        return targetStock ? prevStocks : { ...prevStocks, [nextId]: makeEmptyFetchedStock() }
      }

      return {
        ...prevStocks,
        [BASE_TAB_ID]: makeEmptyFetchedStock(),
        [nextId]: baseStock,
      }
    })

    setTabsState((state) => {
      const tabs = state.tabs
      const newBaseTab: ListingTabMeta = {
        id: nextId,
        title: trimmed === 'Tab 1' ? `Tab ${nextId}` : trimmed,
        lotId: undefined,
      }

      return {
        ...state,
        tabs: [...tabs, newBaseTab],
        activeTabId: nextId,
      }
    })
  }

  const unlinkListingTabFromLot = useCallback(
    (tabId: ListingTabId) => {
      setTabsState((state) => ({
        ...state,
        tabs: state.tabs.map((tab) =>
          tab.id === tabId
            ? {
                ...tab,
                lotId: undefined,
                title: `Tab ${tabId}`,
              }
            : tab
        ),
      }))

      setAllListingStocks((prev) => {
        const newStock = prev[tabId]
        newStock.stock.forEach((article) => {
          article.purchaseEventId = undefined
        })
        newStock.sealedStock.forEach((article) => {
          article.purchaseEventId = undefined
        })

        return {
          ...prev,
          [tabId]: newStock,
        }
      })
    },
    [setTabsState]
  )

  /**
   * When the tab is deleted, it is deleted from both tabState and allListings.
   */
  const closeListingTab = useCallback(
    (id: ListingTabId) => {
      setTabsState((state) => {
        if (id === BASE_TAB_ID && state.tabs.length > 1) {
          setAllListingStocks((prevStocks) => ({
            ...prevStocks,
            [BASE_TAB_ID]: makeEmptyFetchedStock(),
          }))
          return state
        }

        const remaining = state.tabs.filter((tab) => tab.id !== id)

        // Case 1: I'm closing the last tab → I'm starting from scratch
        if (!remaining.length) {
          const initial = makeInitialTabsState()

          // We also reset all the stocks and leave only the initial tab with an empty stock
          setAllListingStocks({
            [initial.activeTabId]: makeEmptyFetchedStock(),
          })

          return initial
        }

        //Case 2: Other tabs remain
        const newActiveId = state.activeTabId === id ? remaining[0].id : state.activeTabId

        // I update the stock map: I remove the stock from the closed tab
        setAllListingStocks((prevStocks) => {
          const { [id]: _removed, ...rest } = prevStocks

          // If the new active tab doesn't have stock yet, I'll give it an empty one
          if (!rest[newActiveId]) {
            return {
              ...rest,
              [newActiveId]: makeEmptyFetchedStock(),
            }
          }

          return rest
        })

        return {
          tabs: remaining,
          activeTabId: newActiveId,
        }
      })
    },
    [setTabsState, setAllListingStocks]
  )

  //if there is an old listing file use that, it will be needed for deployment
  useEffect(() => {
    if (legacyLoading || loadingStocks) return

    const legacyHasData =
      (legacyListing?.stock && legacyListing.stock.length > 0) ||
      (legacyListing?.sealedStock && legacyListing.sealedStock.length > 0)

    const alreadyMigrated = Object.keys(allListingStocks).length > 0

    if (!legacyHasData || alreadyMigrated) return

    setAllListingStocks({
      [tabsState.activeTabId]: {
        ...legacyListing,
        fetchedAt: legacyListing.fetchedAt || new Date().toISOString(),
      },
    })
  }, [
    legacyLoading,
    loadingStocks,
    legacyListing,
    allListingStocks,
    setAllListingStocks,
    tabsState.activeTabId,
  ])

  // Ensures there is always a valid active tab
  useEffect(() => {
    if (!tabsState.tabs.length) {
      setTabsState(makeInitialTabsState())
      return
    }

    const exists = tabsState.tabs.some((tab) => tab.id === tabsState.activeTabId)

    if (!exists) {
      setTabsState((state) => ({
        ...state,
        activeTabId: state.tabs[0].id,
      }))
    }
  }, [tabsState.tabs, tabsState.activeTabId, setTabsState])

  useEffect(() => {
    if (loadingTabs || loadingStocks) return
    if (prunedOnceRef.current) return
    prunedOnceRef.current = true

    const { tabs, activeTabId } = tabsState

    if (!tabs.length) {
      const initial = makeInitialTabsState()
      setTabsState(initial)
      setAllListingStocks({
        [initial.activeTabId]: makeEmptyFetchedStock(),
      })
      return
    }

    const hasContent = (tabId: ListingTabId) => {
      const stockForTab = allListingStocks[tabId]
      const hasSingles = stockForTab?.stock?.length ?? 0
      const hasSealed = stockForTab?.sealedStock?.length ?? 0
      return hasSingles > 0 || hasSealed > 0
    }

    // ALWAYS keep tab 0, even if it's empty
    const prunedTabs = tabs.filter((tab) => (tab.id === BASE_TAB_ID ? true : hasContent(tab.id)))

    // if they are all already "prunedTabs", do nothing
    if (prunedTabs.length === tabs.length) return

    // If for some reason we have lost the base tab: complete reset
    const hasBase = prunedTabs.some((t) => t.id === BASE_TAB_ID)
    if (!hasBase) {
      const initial = makeInitialTabsState()
      setTabsState(initial)
      setAllListingStocks({
        [initial.activeTabId]: makeEmptyFetchedStock(),
      })
      return
    }

    const newActiveId = prunedTabs.some((t) => t.id === activeTabId)
      ? activeTabId
      : prunedTabs[0].id

    const newStocks: Record<ListingTabId, FetchedStockInterface> = {}
    prunedTabs.forEach((tab) => {
      const s = allListingStocks[tab.id]
      newStocks[tab.id] = s ?? makeEmptyFetchedStock()
    })

    setTabsState({
      tabs: prunedTabs,
      activeTabId: newActiveId,
    })
    setAllListingStocks(newStocks)
  }, [loadingTabs, loadingStocks, tabsState, allListingStocks, setTabsState, setAllListingStocks])

  return {
    loadingStocks,
    activeListingTabId,
    tabsState,
    listingStock,
    allListingStocks,
    getNextTabId,
    setTabsState,
    setAllListingStocks,
    makeEmptyFetchedStock,
    setListingStock,
    addListingTab,
    setActiveListingTab,
    renameListingTab,
    closeListingTab,
    unlinkListingTabFromLot,
  }
}
